// ── Polymarket Org Key Panel ────────────────────────────────────
//
// Sibling of OrgKeysPanel for the polymarket venue. Same Add/Delete
// affordances; venue-specific form fields (signer key, signature type,
// funder).
//
// Reuses the hardened validators from src/lib/polymarket/client.ts for
// inline format checks before submit. The relay does its own server-side
// validation and L2 verification — this is purely for UX feedback.

import { useCallback, useEffect, useState } from 'react';
import { Ban, Loader2, Plus, RefreshCw, Trash2, Wallet } from 'lucide-react';
import { apiFetch } from '../../stores/authStore';
import { ConfirmModal } from '../../components/atoms/Modal';
import { isValidEvmAddress, isValidEvmPrivateKey } from '../../lib/polymarket/client';
import {
  CredentialHealthLines,
  CredentialStateBadge,
  type CredentialLifecycle,
  getCredentialState,
  isCredentialEnabled,
} from '../../components/credential/CredentialLifecycle';

interface PolyOrgCredential extends CredentialLifecycle {
  id: string;
  label: string;
  // accessKey holds the signer address for polymarket rows.
  accessKey: string;
  venue: 'kalshi' | 'polymarket';
  environment: 'prod' | 'demo';
  signatureType: 0 | 1 | 2 | 3 | null;
  funder: string | null;
  createdAt: string;
}

interface PolymarketOrgKeysPanelProps {
  orgQueryParam?: string;
  embedded?: boolean;
  className?: string;
}

const SIG_TYPE_LABEL: Record<0 | 1 | 2 | 3, string> = {
  0: 'EOA',
  1: 'Magic',
  2: 'Gnosis',
  3: 'Deposit',
};

/** Signature types selectable in the Add-Key form. EOA (0) is view-only:
 *  Polymarket rejects bare-EOA makers at order placement, and the relay
 *  refuses to store or re-enable type-0 keys. */
type SelectableSigType = 1 | 2 | 3;

interface SigTypeGuide {
  title: string;
  audience: string;
  signer: string;
  funder: string;
  extra?: string;
}

/**
 * Per-type setup guides ("select each one to learn more"). The one
 * invariant across every live type: the signer key only signs; the
 * FUNDER — the wallet address on the account's polymarket.com profile —
 * holds the collateral and is the maker of record. It never equals the
 * signer address. Full model: docs/polymarket-auth-reference.md.
 */
const SIG_TYPE_GUIDE: Record<0 | 1 | 2 | 3, SigTypeGuide> = {
  0: {
    title: 'EOA — legacy, no longer accepted',
    audience:
      'Raw wallets that deposited USDC to their own address in the early programmatic era. Polymarket now rejects every bare-EOA maker at order placement ("maker address not allowed"). Balance reads still work, so these keys look valid at add time and die at the first order — the relay therefore refuses them up front.',
    signer: 'N/A — this type cannot be registered.',
    funder:
      'If you hold one of these keys, its account (if any) lives behind a proxy or deposit wallet. Re-register the same signer under the type that matches how the account was created — see the other tabs.',
  },
  1: {
    title: 'Magic — email-login accounts (pre-deposit-wallet era)',
    audience:
      'Polymarket accounts created by signing up with an EMAIL ADDRESS (Magic-link login) before the deposit-wallet era.',
    signer:
      'Exported from polymarket.com itself: log in with the email account, then profile → settings → export private key. (Magic custodies the key; the site is the only place to get it.)',
    funder:
      'The wallet address shown on that account\'s polymarket.com profile — a Magic proxy contract. It will not match the signer address; that is correct.',
  },
  2: {
    title: 'Gnosis — browser-wallet accounts (pre-deposit-wallet era)',
    audience:
      'Polymarket accounts created by CONNECTING A CRYPTO WALLET — MetaMask, Coinbase Wallet, Rabby, any EVM wallet — before the deposit-wallet era.',
    signer:
      'That wallet\'s own private key, exported from the wallet (MetaMask: account menu → Account details → Show private key). It must be the exact wallet that created the Polymarket account — it owns the Safe.',
    funder:
      'The wallet address shown on the account\'s polymarket.com profile — the Gnosis Safe holding the USDC. It will not match the signer address; that is correct.',
  },
  3: {
    title: 'Deposit Wallet — every current-flow account',
    audience:
      'Any account onboarded through the current polymarket.com flow (2025+), regardless of login method. Unsure of your account\'s vintage? Try this type first, then Gnosis, then Magic — the add-time check rejects wrong pairings quickly.',
    signer:
      'Exported from polymarket.com: log in, then profile → settings → export private key.',
    funder:
      'The deposit-wallet address shown on the account\'s polymarket.com profile (derived at onboarding).',
    extra:
      'Deposit-wallet specifics: collateral is pUSD, and after EVERY deposit you must click "Allowance" on the key row below — otherwise the CLOB caches a zero balance and rejects orders.',
  },
};

export function PolymarketOrgKeysPanel({
  orgQueryParam = '',
  embedded = false,
  className,
}: PolymarketOrgKeysPanelProps) {
  const [creds, setCreds] = useState<PolyOrgCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [label, setLabel] = useState('');
  const [signerPk, setSignerPk] = useState('');
  // Default to Gnosis Safe, matching the common existing browser-wallet setup.
  const [signatureType, setSignatureType] = useState<SelectableSigType>(2);
  // Which type's guide is expanded. Follows selection, but EOA's guide is
  // viewable (to explain WHY it's dead) without being selectable.
  const [guideType, setGuideType] = useState<0 | 1 | 2 | 3>(2);
  const [funder, setFunder] = useState('');
  const [hardDeleteTarget, setHardDeleteTarget] = useState<PolyOrgCredential | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  // Last-action feedback for the per-row "Refresh allowance" button. Shows
  // the fresh balance + allowance, or an error blurb, until the next click
  // or any other interaction.
  const [refreshState, setRefreshState] = useState<
    | { kind: 'idle' }
    | { kind: 'pending' }
    | { kind: 'ok'; balance: number; allowance: number }
    | { kind: 'err'; message: string }
  >({ kind: 'idle' });

  const enabledCount = creds.filter(isCredentialEnabled).length;

  const handleRefreshAllowance = useCallback(async (credentialId: string) => {
    setRefreshState({ kind: 'pending' });
    try {
      const res = await apiFetch(
        `/api/manager/credentials/${encodeURIComponent(credentialId)}/refresh-allowance${orgQueryParam}`,
        { method: 'POST' },
      );
      const body = (await res.json().catch(() => ({}))) as {
        balance?: { collateralUsd?: unknown; collateralAllowanceUsd?: unknown };
        error?: string;
      };
      if (!res.ok) {
        setRefreshState({
          kind: 'err',
          message: body.error ?? `Refresh failed (${res.status})`,
        });
        return;
      }
      const balance = body.balance ?? {};
      setRefreshState({
        kind: 'ok',
        balance: Number(balance.collateralUsd) || 0,
        allowance: Number(balance.collateralAllowanceUsd) || 0,
      });
    } catch {
      setRefreshState({ kind: 'err', message: 'Network error' });
    }
  }, [orgQueryParam]);

  // Single GET returns all venues; filter client-side for the polymarket subset.
  const fetchCreds = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/credentials${orgQueryParam}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Failed to load org keys');
        setCreds([]);
      } else {
        const all = (data.credentials ?? []) as PolyOrgCredential[];
        setCreds(all.filter((c) => c.venue === 'polymarket'));
      }
    } catch {
      setError('Network error');
    }
    setLoading(false);
  }, [orgQueryParam]);

  useEffect(() => {
    fetchCreds();
  }, [fetchCreds]);

  // Inline format validation — matches what the relay enforces.
  const pkTrim = signerPk.trim();
  const funderTrim = funder.trim();
  const pkFormatError = pkTrim && !isValidEvmPrivateKey(pkTrim)
    ? 'Must be a valid 0x + 64-hex EVM private key.'
    : null;
  const funderFormatError = funderTrim && !isValidEvmAddress(funderTrim)
    ? 'Must be a valid 0x address (40 hex chars).'
    : null;
  const funderMissing = !funderTrim;
  const labelOk = !!label.trim();
  const canSubmit =
    labelOk && !!pkTrim && !pkFormatError && !funderFormatError && !funderMissing && !submitting;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/credentials${orgQueryParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venue: 'polymarket',
          label: label.trim(),
          signerPk: pkTrim,
          signatureType,
          funder: funderTrim,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Failed to add Polymarket key');
      } else {
        setCreds((prev) => [...prev, data.credential]);
        setLabel('');
        setSignerPk('');
        setFunder('');
        setShowForm(false);
      }
    } catch {
      setError('Network error');
    }
    setSubmitting(false);
  };

  const handlePatchCredential = useCallback(async (
    credential: PolyOrgCredential,
    body: { disabled?: boolean; release?: boolean },
  ) => {
    setActingId(credential.id);
    setError(null);
    const res = await apiFetch(`/api/manager/credentials/${credential.id}${orgQueryParam}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({})) as {
      credential?: PolyOrgCredential;
      error?: string;
    };
    if (res.ok) {
      setCreds((prev) => prev.map((c) => (c.id === credential.id ? data.credential ?? c : c)));
    } else {
      setError(data.error ?? 'Failed to update key');
    }
    setActingId(null);
  }, [orgQueryParam]);

  const handleHardDelete = async () => {
    if (!hardDeleteTarget) return;
    const id = hardDeleteTarget.id;
    setHardDeleteTarget(null);
    setActingId(id);
    const res = await apiFetch(credentialUrl(
      `/api/manager/credentials/${id}`,
      orgQueryParam,
      { hard: 'true', confirm: 'delete' },
    ), { method: 'DELETE' });
    if (res.ok) {
      setCreds((prev) => prev.filter((c) => c.id !== id));
    } else {
      const data = await res.json().catch(() => ({})) as { error?: string };
      setError(data.error ?? 'Failed to delete key');
    }
    setActingId(null);
  };

  const truncate = (s: string, n = 10): string => (s.length <= n ? s : `${s.slice(0, n)}…`);

  const Wrapper = embedded ? 'div' : 'section';
  const wrapperClassName = embedded
    ? `min-w-0 ${className ?? ''}`
    : `bg-card border-border rounded-lg border p-4 ${className ?? 'mb-6'}`;

  return (
    <Wrapper className={wrapperClassName}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wallet size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">Org Polymarket Keys</h3>
          <span className="text-muted-foreground text-[11px]">
            ({creds.length === 0 ? 'none' : `${enabledCount}/${creds.length} enabled`})
          </span>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500"
        >
          <Plus size={14} /> {showForm ? 'Cancel' : 'Add Key'}
        </button>
      </div>

      <p className="text-muted-foreground mb-3 text-[11px]">
        Multiple Polymarket signer keys can be stored for the org pool — but every key must
        sign for the SAME Polymarket account (funder): orders are placed by whichever pool key
        rotation lands on, and positions live on the account. Auth-style credential failures
        fall through to the next key. The signer private key is encrypted at rest and never
        shown to the browser again after upload. Supports Magic, Gnosis, and Deposit Wallet
        signatures — legacy EOA keys are no longer accepted by Polymarket. One Polymarket
        account per org: account-level positions and fills bleed attribution across orgs.
      </p>

      {error && (
        <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
      )}

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="bg-muted/50 border-border mb-3 space-y-2 rounded-md border p-3"
        >
          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">Label</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="bg-card border-border text-foreground w-full rounded-md border px-2 py-1 text-xs focus:border-blue-500 focus:outline-none"
              placeholder="e.g. prod-2026-q2"
            />
          </div>

          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">
              Signer Private Key (hex)
            </label>
            <input
              type="password"
              value={signerPk}
              onChange={(e) => setSignerPk(e.target.value)}
              aria-invalid={!!pkFormatError}
              className={`bg-card text-foreground w-full rounded-md border px-2 py-1 font-mono text-[10px] focus:outline-none ${
                pkFormatError
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-border focus:border-blue-500'
              }`}
              placeholder="0x..."
            />
            {pkFormatError && (
              <div className="mt-1 text-[11px] text-red-400">{pkFormatError}</div>
            )}
          </div>

          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">Signature Type</label>
            <div className="flex gap-1">
              <button
                type="button"
                aria-disabled
                onClick={() => setGuideType(0)}
                title="Legacy — Polymarket no longer accepts EOA makers. Click to learn why."
                className={`rounded-md px-2 py-1 text-[11px] font-medium line-through decoration-red-400/70 ${
                  guideType === 0
                    ? 'bg-red-500/10 text-red-400 ring-1 ring-red-500/40'
                    : 'bg-secondary text-muted-foreground/60 hover:text-red-400'
                }`}
              >
                {SIG_TYPE_LABEL[0]}
              </button>
              {([1, 2, 3] as const).map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => {
                    setSignatureType(t);
                    setGuideType(t);
                  }}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium ${
                    signatureType === t
                      ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/40'
                      : 'bg-secondary text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {SIG_TYPE_LABEL[t]}
                </button>
              ))}
            </div>
            <p className="text-muted-foreground mt-1 text-[11px]">
              Select each type to learn how to set it up. The type must match how the
              account was created on polymarket.com — it is not a preference.
            </p>
            <div
              className={`mt-2 space-y-1.5 rounded-md border p-2.5 text-[11px] ${
                guideType === 0
                  ? 'border-red-500/30 bg-red-500/5'
                  : 'border-border/60 bg-card/60'
              }`}
            >
              <div className={`font-medium ${guideType === 0 ? 'text-red-400' : 'text-foreground'}`}>
                {SIG_TYPE_GUIDE[guideType].title}
              </div>
              <p className="text-muted-foreground">
                <span className="text-foreground/80 font-medium">Who this is for: </span>
                {SIG_TYPE_GUIDE[guideType].audience}
              </p>
              <p className="text-muted-foreground">
                <span className="text-foreground/80 font-medium">Signer private key: </span>
                {SIG_TYPE_GUIDE[guideType].signer}
              </p>
              <p className="text-muted-foreground">
                <span className="text-foreground/80 font-medium">Funder address: </span>
                {SIG_TYPE_GUIDE[guideType].funder}
              </p>
              {SIG_TYPE_GUIDE[guideType].extra && (
                <p className="text-amber-500/90 dark:text-amber-400/90">
                  {SIG_TYPE_GUIDE[guideType].extra}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">
              Funder Address <span className="text-red-400">*</span>
            </label>
            <input
              value={funder}
              onChange={(e) => setFunder(e.target.value)}
              aria-invalid={!!funderFormatError}
              className={`bg-card text-foreground w-full rounded-md border px-2 py-1 font-mono text-[10px] focus:outline-none ${
                funderFormatError
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-border focus:border-blue-500'
              }`}
              placeholder="0x..."
            />
            {funderFormatError ? (
              <div className="mt-1 text-[11px] text-red-400">{funderFormatError}</div>
            ) : (
              <p className="text-muted-foreground mt-1 text-[11px]">
                The address shown on the org's Polymarket profile — the proxy/Safe/deposit wallet that holds the collateral, not the address derived from the signer key.
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {submitting && <Loader2 size={12} className="animate-spin" />} Save Key
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
        </div>
      ) : creds.length === 0 ? (
        <p className="text-muted-foreground py-2 text-center text-xs">
          No Polymarket org key yet. Add one to let org traders place Polymarket orders via the relay.
        </p>
      ) : (
        <div className="space-y-2">
          {creds.map((c) => {
            const state = getCredentialState(c);
            const disabled = state.kind === 'disabled';
            const canRelease =
              !disabled &&
              (!!c.quarantinedUntil ||
                Number(c.consecutiveAuthFailures ?? 0) > 0 ||
                !!c.lastErrorAt);
            const busy = actingId === c.id;
            return (
              <div
                key={c.id}
                className={`border-border/60 bg-muted/30 rounded-md border px-3 py-2 ${
                  disabled ? 'opacity-75' : ''
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                    <span className="text-foreground text-xs font-medium">{c.label}</span>
                    <CredentialStateBadge credential={c} />
                    <span
                      title={
                        c.signatureType === 0
                          ? 'Legacy EOA — Polymarket no longer accepts these; excluded from the org pool. Re-register with the correct type + funder.'
                          : undefined
                      }
                      className={`rounded px-1.5 py-0.5 text-[10px] ${
                        c.signatureType === 0
                          ? 'bg-red-500/10 text-red-400'
                          : 'bg-secondary text-muted-foreground'
                      }`}
                    >
                      {c.signatureType === null
                        ? 'Unknown'
                        : c.signatureType === 0
                          ? 'EOA · legacy, dead'
                          : SIG_TYPE_LABEL[c.signatureType]}
                    </span>
                    <span className="text-muted-foreground/40">·</span>
                    <span className="font-mono">Signer {truncate(c.accessKey)}</span>
                    <span className="text-muted-foreground/40">·</span>
                    <span className="font-mono">Funder {c.funder ? truncate(c.funder) : '-'}</span>
                    <span className="text-muted-foreground/40">·</span>
                    <span>Added {new Date(c.createdAt).toLocaleDateString()}</span>
                    <CredentialHealthLines credential={c} inline />
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                    {c.signatureType === 3 && !disabled && (
                      <button
                        onClick={() => handleRefreshAllowance(c.id)}
                        disabled={refreshState.kind === 'pending' || busy}
                        title="Refresh on-chain balance + allowance (required after deposits for sigType=3)"
                        className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-blue-500/10 hover:text-blue-400 disabled:opacity-50"
                      >
                        {refreshState.kind === 'pending' ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <RefreshCw size={12} />
                        )}
                        Allowance
                      </button>
                    )}
                    {canRelease && (
                      <button
                        onClick={() => handlePatchCredential(c, { release: true })}
                        disabled={busy}
                        title="Release quarantine and reset auth failure count"
                        className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-blue-500/10 hover:text-blue-400 disabled:opacity-50"
                      >
                        <RefreshCw size={12} className={busy ? 'animate-spin' : ''} />
                        Release
                      </button>
                    )}
                    {/* Legacy EOA rows can be disabled but never re-enabled —
                        the relay rejects the enable and the pool skips them. */}
                    {!(disabled && c.signatureType === 0) && (
                      <button
                        onClick={() => handlePatchCredential(c, { disabled: !disabled })}
                        disabled={busy}
                        title={disabled ? 'Enable key' : 'Disable key'}
                        className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-muted hover:text-foreground disabled:opacity-50"
                      >
                        <Ban size={12} />
                        {disabled ? 'Enable' : 'Disable'}
                      </button>
                    )}
                    {disabled && (
                      <button
                        onClick={() => setHardDeleteTarget(c)}
                        disabled={busy}
                        title="Permanently delete key"
                        className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
                      >
                        <Trash2 size={12} />
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {refreshState.kind === 'ok' && (
        <div className="mt-3 rounded-md bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-500 dark:text-emerald-400">
          Refreshed: balance ${refreshState.balance.toFixed(2)}, allowance ${refreshState.allowance.toFixed(2)}.
        </div>
      )}
      {refreshState.kind === 'err' && (
        <div className="mt-3 rounded-md bg-red-500/10 px-3 py-2 text-[11px] text-red-500 dark:text-red-400">
          Refresh failed: {refreshState.message}
        </div>
      )}

      <ConfirmModal
        isOpen={!!hardDeleteTarget}
        onClose={() => setHardDeleteTarget(null)}
        onConfirm={handleHardDelete}
        title="Permanently Delete Polymarket Org Key"
        message={`Permanently delete "${hardDeleteTarget?.label}"? This removes the encrypted signer key and lifecycle history. Disable is safer unless this key should never be recoverable.`}
        confirmLabel="Delete Permanently"
        variant="danger"
      />
    </Wrapper>
  );
}

function credentialUrl(
  path: string,
  orgQueryParam: string,
  extra: Record<string, string>,
): string {
  const params = new URLSearchParams(orgQueryParam ? orgQueryParam.slice(1) : '');
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}
